import { TagsEditor } from '@lmt/unicorn-shared/app/components/common/ui-kit/tags/TagsEditor';
import React, { useCallback, useRef, useState } from 'react';
import { useTriggerOnClickOutsideElement } from '@lmt/unicorn-shared/app/helpers/useTriggerOnClickOutsideElement';
import { DefaultProps, MandatoryProps } from './types';
import { InputTemplateOptionList } from '../../../input-templates/list/InputTemplateOptionList';
import { Popper, useEventCallback } from '@mui/material';

type TextInputProps = MandatoryProps &
  Partial<DefaultProps> & {
    text: string;
    setText: (value: string) => void;
    setDataBlockFieldValue?: (key: string, value: string | undefined) => void;
    error?: string;
    popperClassName?: string;
  };
export const EditableTextInput: React.FC<TextInputProps> = React.memo(
  function EditableTextInput(props: TextInputProps) {
    const { text, setText, setDataBlockFieldValue, error } = props;
    const [expanded, setExpanded] = useState<boolean>(false);
    const optionListRef = useRef<HTMLDivElement>(null);
    const inputRef = useRef<HTMLTextAreaElement>(null);
    const hide = useCallback(() => setExpanded(false), [setExpanded]);
    useTriggerOnClickOutsideElement(optionListRef, hide, expanded);

    const onTemplateClickHandler = useCallback(() => {
      setExpanded((oldExpandedValue) => {
        return !oldExpandedValue;
      });
    }, []);

    const insertTemplate = useEventCallback((value: string) => {
      function setValue(newValue: string) {
        setText(newValue);
        setDataBlockFieldValue?.(props.dataKey, newValue);
      }

      const currentRef = inputRef.current;
      if (!currentRef) {
        setValue(concatenateText(value, text));
        return;
      }

      const startPosition = currentRef.selectionStart ?? 0;
      const endPosition = currentRef.selectionEnd ?? 0;
      const newValue = !text
        ? value
        : text.substring(0, startPosition) +
          value +
          text.substring(endPosition);
      setValue(newValue);

      const newPosition = startPosition + value.length;
      setTimeout(() => {
        currentRef.focus();
        currentRef.setSelectionRange(newPosition, newPosition);
      }, 0);
    });

    return (
      <div>
        <TagsEditor
          value={text}
          onChange={(e, value) => {
            setText(value);
            setDataBlockFieldValue?.(props.dataKey, value);
          }}
          error={error !== ''}
          helperText={error}
          isMultiline={props.isMultiline ?? false}
          maxAutoHeight={100}
          isTemplateVisible={expanded}
          onTemplateClick={
            props.dataBlockFieldId ? onTemplateClickHandler : undefined
          }
          ref={inputRef}
        />
        <Popper
          className={props.popperClassName}
          open={expanded}
          placement="bottom-start"
          anchorEl={inputRef.current}
          sx={{ width: inputRef.current?.offsetWidth }}
        >
          <InputTemplateOptionList
            fieldId={props.dataBlockFieldId}
            fieldName={props.dataKey}
            onOptionClick={(value: string) => {
              setExpanded(false);
              insertTemplate(value);
            }}
            elementRef={optionListRef}
            onClose={() => setExpanded(false)}
            isMultipleSelect={true}
            isNumber={false}
            isMultiline={props.isMultiline ?? false}
          />
        </Popper>
      </div>
    );
  },
);

function concatenateText(newData: string, oldData: string | null | undefined) {
  if (!oldData) {
    return newData;
  }
  const lastLine = oldData.split('\n').pop();
  if (!lastLine) {
    return oldData + newData;
  }

  return oldData.trimEnd() + ' ' + newData;
}
